import { KeyboardEventHandler, useEffect, useRef } from 'react';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import debounce from 'lodash/debounce';
import { SendButton } from '~/components/Chat/Chat01/TextBox/TextBox.styled';
import { Icon, Input } from '~/components/atomic';
import {
  useCreateCommentMutation,
  useCreateGameCommentMutation,
  useEditCommentMutation,
  useEditGameCommentMutation,
} from '~/features/comment';
import {
  CommentSort,
  CreateCommentRequest,
  EditCommentRequest,
  GetCommentData,
} from '~/types';
import {
  CreateGameCommentRequest,
  UpdateGameCommentRequest,
} from '~/types/missionGameApi';

interface CommentInputProps {
  target_id: string;
  isCommentOpen: boolean;
  commentInputState: string;
  setCommentInputState: React.Dispatch<React.SetStateAction<string>>;

  editingComment: GetCommentData | null;
  setEditingComment: React.Dispatch<
    React.SetStateAction<GetCommentData | null>
  >;
  isEditMode: boolean;
  sort: CommentSort;
  isMissionGame: boolean;
}

export default function CommentInput({
  target_id,
  isCommentOpen,
  commentInputState,
  setCommentInputState,
  editingComment,
  setEditingComment,
  isEditMode,
  sort,
  isMissionGame,
}: CommentInputProps) {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isCommentSendEnabled = isEditMode
    ? commentInputState.trim() !== '' &&
      commentInputState !== editingComment?.content
    : commentInputState.trim() !== '';

  const { mutate: createCommentMutate, isPending: createPending } =
    // useCreateCommentMutation(sort, state?.type || 'mission');
    useCreateCommentMutation(sort);

  const { mutate: createGameCommentMutate, isPending: createGamePending } =
    useCreateGameCommentMutation(sort);

  const { mutate: editCommentMutate, isPending: editPending } =
    useEditCommentMutation();
  const { mutate: editGameCommentMutate, isPending: editGamePending } =
    useEditGameCommentMutation();

  // Edit mode input focus handling
  useEffect(() => {
    if (isEditMode && editingComment) {
      setCommentInputState('');
      setCommentInputState(editingComment.content);
    }
  }, [isEditMode, editingComment, setCommentInputState]);

  useEffect(() => {
    if ((isEditMode || isCommentOpen) && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.selectionStart = inputRef.current.value.length;
      inputRef.current.selectionEnd = inputRef.current.value.length;
    }
  }, [isEditMode, isCommentOpen]);

  const handleCommentSendClick = debounce(async () => {
    if (!isCommentSendEnabled || isSubmitting) return;

    setIsSubmitting(true);
    setCommentInputState('');

    try {
      if (isEditMode && editingComment) {
        if (isMissionGame) {
          const requestData: UpdateGameCommentRequest & {
            game_id: number;
          } = {
            comment: commentInputState,
            comment_id: editingComment.comment_id,
            game_id: Number(target_id),
          };
          editGameCommentMutate(
            {
              newComment: requestData,
            },
            {
              onSuccess({ status }) {
                setEditingComment(null);
              },
            },
          );
        } else {
          const requestData: EditCommentRequest = {
            character_id: target_id,
            content: commentInputState,
            comment_id: editingComment.comment_id,
          };
          editCommentMutate(requestData, {
            onSuccess({ status }) {
              setEditingComment(null);
            },
          });
        }
      } else {
        if (isMissionGame) {
          const requestData: CreateGameCommentRequest = {
            game_id: Number(target_id),
            comment: commentInputState,
          };
          createGameCommentMutate(requestData);
        } else {
          const requestData: CreateCommentRequest = {
            character_id: target_id,
            content: commentInputState,
          };

          createCommentMutate(requestData);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  }, 100);

  const handleInputKeyDown: KeyboardEventHandler<HTMLInputElement> = (
    event,
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleCommentSendClick();
    }
  };

  return (
    <Container>
      <Input
        id='comment'
        ref={inputRef}
        text={commentInputState}
        setText={setCommentInputState}
        placeholder='Add a comment'
        isFullWidth
        inputWrapperName='comment-wrapper'
        maxText={300}
        autoCorrect='off'
        type='text'
        // @ts-ignore
        autoComplete='off'
        onKeyDown={handleInputKeyDown}
      />

      <CommentSaveButton
        onClick={handleCommentSendClick}
        disabled={!isCommentSendEnabled}
      >
        <Icon
          size={28}
          color={isCommentSendEnabled ? 'content_dark_1' : 'content_light_5'}
          iconName='Send'
        />
      </CommentSaveButton>
    </Container>
  );
}

export const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    width: 100%;
    height: 64px;
    padding: 0 ${theme.layout.section_side};
    border-top: 2px solid ${theme.stroke.stroke_1};
    background: ${theme.backgroundColor.bg_dark_2};
    box-shadow: 0px -30px 48px 0px rgba(0, 0, 0, 0.5);
    gap: 13px;

    .comment-wrapper {
      background: ${theme.backgroundColor.bg_dark_5_alpha};
    }
  `}
`;

export const CommentSaveButton = styled(SendButton)`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
`;
